import React, { useCallback, useEffect, useState } from 'react';
import { Play, Trophy, Sparkles, Loader2 } from 'lucide-react';
import { api } from '../lib/api';
import LotteryWheelDialog from './LotteryWheelDialog';

const SPIN_MS = 4600;
const REVEAL_MS = 1500;

/**
 * HomepageDrawReplay — public replay of whatever draw the admin has "featured".
 *
 * Renders a compact card advertising the last drawing. Clicking "Watch it"
 * opens the same wheel modal used in the admin panel and replays the exact
 * same spin sequence deterministically from the stored pool + winners
 * snapshot. No video files, no upload — just a re-run of the animation.
 */
export default function HomepageDrawReplay() {
  const [draw, setDraw] = useState(null);
  const [loading, setLoading] = useState(true);
  const [wheel, setWheel] = useState({
    open: false, phase: 'idle', winners: [], targetCount: 0,
    pool: [], targetUserId: null, spinKey: 0,
  });

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const d = await api.getFeaturedDraw();
        if (!cancelled) setDraw(d || null);
      } catch { /* silently ignore */ }
      finally { if (!cancelled) setLoading(false); }
    })();
    return () => { cancelled = true; };
  }, []);

  const play = useCallback(async () => {
    if (!draw?.winners?.length) return;
    const pool = draw.pool_snapshot?.length ? draw.pool_snapshot : draw.winners.map((w) => ({
      user_id: w.user_id, username: w.username, name: w.name, total_entries: w.entries,
    }));
    setWheel({
      open: true, phase: 'spinning', winners: [], targetCount: draw.winners.length,
      pool, targetUserId: null, spinKey: 0,
    });
    for (let i = 0; i < draw.winners.length; i++) {
      const w = draw.winners[i];
      setWheel((prev) => ({
        ...prev, phase: 'spinning',
        targetUserId: w.user_id, spinKey: prev.spinKey + 1,
      }));
      await new Promise((r) => setTimeout(r, SPIN_MS));
      setWheel((prev) => ({
        ...prev, phase: 'reveal', winners: [...prev.winners, w],
      }));
      await new Promise((r) => setTimeout(r, REVEAL_MS));
    }
    setWheel((prev) => ({ ...prev, phase: 'done' }));
  }, [draw]);

  if (loading) return null;
  if (!draw) return null; // nothing featured — hide the card entirely

  const drawnAtLabel = new Date(draw.drawn_at).toLocaleString(undefined, {
    dateStyle: 'medium', timeStyle: 'short',
  });

  return (
    <section
      data-testid="featured-draw-section"
      className="max-w-7xl mx-auto px-3 sm:px-6 mt-4 sm:mt-6"
    >
      <div
        className="rounded-2xl border-[3px] border-yellow-300 shadow-[0_5px_0_#3a1064] overflow-hidden"
        style={{ background: 'linear-gradient(135deg,#1a0440 0%,#3a1064 50%,#4c1090 100%)' }}
      >
        <div className="p-4 sm:p-5 flex items-center gap-3 flex-wrap">
          <div className="h-11 w-11 sm:h-12 sm:w-12 rounded-2xl bg-[#ffd900] border-2 border-yellow-300 shadow-[0_3px_0_#a4007f] flex items-center justify-center shrink-0">
            <Trophy className="h-6 w-6 text-[#3a1064]" strokeWidth={3} />
          </div>
          <div className="min-w-0 flex-1">
            <div className="text-[10px] sm:text-xs font-black uppercase tracking-[2px] text-yellow-300/90">
              Latest V-Bucks drawing
            </div>
            <div className="font-black text-white text-base sm:text-xl leading-tight">
              {draw.winners.length} winner{draw.winners.length === 1 ? '' : 's'} · {draw.total_entries.toLocaleString()} entries
            </div>
            <div className="text-white/70 font-bold text-[11px] sm:text-xs">
              Drawn {drawnAtLabel} · Prize {draw.weekly_prize.toLocaleString()} V-Bucks
            </div>
          </div>
          <button
            data-testid="watch-drawing-btn"
            onClick={play}
            className="h-11 px-4 sm:px-5 rounded-xl bg-[#ff2bd6] hover:bg-[#e119bf] text-white border-2 border-yellow-300 shadow-[0_3px_0_#7a0060] font-black text-sm flex items-center gap-2 shrink-0 signin-watch"
          >
            <Play className="h-4 w-4" strokeWidth={3} /> Watch the drawing
          </button>
        </div>

        {/* Winner previews (public congrats strip) */}
        <div className="border-t-2 border-yellow-300/25 bg-black/25 p-3 sm:p-4 flex flex-wrap gap-2">
          {draw.winners.map((w) => (
            <div
              key={`fw-${w.tier}`}
              data-testid={`featured-winner-${w.tier}`}
              className="rounded-xl border-2 border-yellow-300 bg-[#3a1064]/70 text-white px-3 py-2 flex items-center gap-2"
            >
              <div className="h-7 w-7 rounded-full bg-[#ffd900] border-2 border-[#3a1064] text-[#3a1064] font-black text-xs flex items-center justify-center">
                {w.tier}
              </div>
              <div className="min-w-0">
                <div className="font-black text-sm truncate max-w-[140px]">
                  {w.name || w.username || 'Anonymous'}
                </div>
                <div className="text-[10px] font-bold text-yellow-300/90 truncate">
                  {w.username && `@${w.username}`} · {w.prize_vbucks.toLocaleString()} V-Bucks
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      <LotteryWheelDialog
        open={wheel.open}
        onClose={() => setWheel((prev) => ({ ...prev, open: false }))}
        pool={wheel.pool}
        winners={wheel.winners}
        targetCount={wheel.targetCount}
        phase={wheel.phase}
        spinKey={wheel.spinKey}
        targetUserId={wheel.targetUserId}
      />

      <style>{`
        @keyframes svWatchPulse {
          0%,100% { transform: translateY(0); }
          50%     { transform: translateY(-2px); }
        }
        .signin-watch { animation: svWatchPulse 1.8s ease-in-out infinite; }
        @media (prefers-reduced-motion: reduce) { .signin-watch { animation: none; } }
      `}</style>
    </section>
  );
}
